import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { largestRemainder } from "../../../views/customers/components/Policies/CommAutomobilePolicy/RiskProfile/IftaImport/utils/largestRemainder";
import {
  previewIftaImportAsync,
  setIftaMode,
  setIftaFiles,
  ackIftaWarning,
  applyIftaEdit,
  resetIftaImport,
} from "./iftaImportActions";
import {
  IIftaImportPreview,
  IIftaProposedSections,
  ImportMode,
  initialIftaImportState,
} from "./iftaImport.types";

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit/property testing)
// ---------------------------------------------------------------------------

/**
 * Parse a dotted/bracketed path string into its individual segments.
 * Supports both `a.b.c` and `a[0].b` notation, e.g.
 * `"travel_details.usa_states[2].quarterly_km.Q3_2025"` →
 * `["travel_details", "usa_states", "2", "quarterly_km", "Q3_2025"]`.
 */
export function parsePath(path: string): string[] {
  return path
    .replace(/\[(\w+)\]/g, ".$1")
    .split(".")
    .filter((segment) => segment.length > 0);
}

/**
 * Immutable path-set: returns a structurally new object with `value` written at
 * `path`, cloning only the nodes along the path and leaving every other node
 * referentially unchanged. Does not mutate `target`.
 */
export function immutableSet<T>(target: T, path: string, value: unknown): T {
  const segments = parsePath(path);
  if (segments.length === 0) {
    return value as T;
  }

  const clone = (node: any, index: number): any => {
    const key = segments[index];
    const isLast = index === segments.length - 1;

    // Determine whether this level should be an array or object.
    const base = Array.isArray(node)
      ? [...node]
      : { ...(node ?? {}) };

    if (isLast) {
      base[key] = value;
      return base;
    }

    base[key] = clone(node == null ? undefined : node[key], index + 1);
    return base;
  };

  return clone(target, 0) as T;
}

/** Safely read a numeric value, treating null/undefined/NaN as 0. */
const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Sum the values of a quarterly_km record. */
const sumQuarterly = (quarterly: Record<string, unknown> | null | undefined): number => {
  if (!quarterly) {
    return 0;
  }
  return Object.values(quarterly).reduce<number>(
    (acc, value) => acc + toNumber(value),
    0,
  );
};

/**
 * Recompute all derived figures in `proposed` after a quarterly km edit:
 * - each travel row's `total_km` (sum of its quarterly values)
 * - `pct_of_country` per country (Canada / USA) via largestRemainder
 * - `pct_of_total` across all rows of both countries via largestRemainder
 * - territory aggregates `canada_km`, `usa_km`, `[canada_pct, usa_pct]`
 * - each quarterly_breakdown row's `pct_of_annual`
 *
 * Returns a new `proposed` object; does not mutate the input.
 */
export function recomputeProposed(
  proposed: IIftaProposedSections,
): IIftaProposedSections {
  if (!proposed) {
    return proposed;
  }

  const travel = proposed.travel_details ?? {};
  const usaStates: any[] = Array.isArray(travel.usa_states)
    ? travel.usa_states
    : [];
  const canadaProvinces: any[] = Array.isArray(travel.canada_provinces)
    ? travel.canada_provinces
    : [];

  // 1. Recompute per-row total_km from quarterly_km.
  const usaWithTotals = usaStates.map((row) => ({
    ...row,
    total_km: sumQuarterly(row?.quarterly_km),
  }));
  const canadaWithTotals = canadaProvinces.map((row) => ({
    ...row,
    total_km: sumQuarterly(row?.quarterly_km),
  }));

  // 2. pct_of_country per country.
  const usaCountryPct = largestRemainder(
    usaWithTotals.map((row) => toNumber(row.total_km)),
  );
  const canadaCountryPct = largestRemainder(
    canadaWithTotals.map((row) => toNumber(row.total_km)),
  );

  // 3. pct_of_total across all rows of both countries (Canada first, then USA).
  const allRows = [...canadaWithTotals, ...usaWithTotals];
  const totalPct = largestRemainder(
    allRows.map((row) => toNumber(row.total_km)),
  );
  const canadaCount = canadaWithTotals.length;

  const recomputedCanada = canadaWithTotals.map((row, index) => ({
    ...row,
    pct_of_country: canadaCountryPct[index] ?? 0,
    pct_of_total: totalPct[index] ?? 0,
  }));
  const recomputedUsa = usaWithTotals.map((row, index) => ({
    ...row,
    pct_of_country: usaCountryPct[index] ?? 0,
    pct_of_total: totalPct[canadaCount + index] ?? 0,
  }));

  // 4. Territory aggregates.
  const canadaKm = recomputedCanada.reduce(
    (acc, row) => acc + toNumber(row.total_km),
    0,
  );
  const usaKm = recomputedUsa.reduce(
    (acc, row) => acc + toNumber(row.total_km),
    0,
  );
  const [canadaPct, usaPct] = largestRemainder([canadaKm, usaKm]);

  const territoryDetails = proposed.territory_covered_details ?? {};
  const territory = territoryDetails.territory ?? {};
  const quarterlyBreakdown: any[] = Array.isArray(territory.quarterly_breakdown)
    ? territory.quarterly_breakdown
    : [];

  const annualPct = largestRemainder(
    quarterlyBreakdown.map((q) => toNumber(q?.total_km)),
  );
  const recomputedBreakdown = quarterlyBreakdown.map((q, index) => ({
    ...q,
    pct_of_annual: annualPct[index] ?? 0,
  }));

  return {
    ...proposed,
    travel_details: {
      ...travel,
      canada_provinces: recomputedCanada,
      usa_states: recomputedUsa,
    },
    territory_covered_details: {
      ...territoryDetails,
      territory: {
        ...territory,
        canada_km: canadaKm,
        usa_km: usaKm,
        canada_pct: canadaPct ?? 0,
        usa_pct: usaPct ?? 0,
        quarterly_breakdown: recomputedBreakdown,
      },
    },
  };
}

/**
 * Build the acknowledgement key for a warning. Combines `key` with `file_name`
 * when the warning carries one so per-file warnings are tracked independently.
 */
export function buildAckKey(key: string, fileName?: string): string {
  return fileName ? `${key}::${fileName}` : key;
}

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const iftaImportSlice = createSlice({
  name: "iftaImport",
  initialState: initialIftaImportState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // --- Preview thunk ---------------------------------------------------
      .addCase(previewIftaImportAsync.pending, (state) => {
        state.stage = "previewing";
        state.error = null;
      })
      .addCase(
        previewIftaImportAsync.fulfilled,
        (state, action: PayloadAction<IIftaImportPreview>) => {
          state.stage = "reviewing";
          state.preview = action.payload;
          state.acknowledgedWarnings = {};
        },
      )
      .addCase(previewIftaImportAsync.rejected, (state, action) => {
        state.stage = "error";
        state.error = action.payload?.message ?? "Failed to preview IFTA import";
        if (action.payload?.status === 404) {
          state.previewUnavailable = true;
        }
      })

      // --- Sync actions ----------------------------------------------------
      .addCase(setIftaMode, (state, action: PayloadAction<ImportMode>) => {
        state.mode = action.payload;
      })
      .addCase(setIftaFiles, (state, action: PayloadAction<File[]>) => {
        state.files = action.payload;
      })
      .addCase(
        ackIftaWarning,
        (state, action: PayloadAction<{ key: string; file_name?: string }>) => {
          const ackKey = buildAckKey(action.payload.key, action.payload.file_name);
          state.acknowledgedWarnings[ackKey] = true;
        },
      )
      .addCase(
        applyIftaEdit,
        (
          state,
          action: PayloadAction<{ path: string; value: unknown }>,
        ) => {
          if (!state.preview) {
            return;
          }
          const { path, value } = action.payload;
          // Immutable path-set into proposed, then recompute derived figures.
          const nextProposed = immutableSet(
            state.preview.proposed,
            path,
            value,
          );
          state.preview.proposed = recomputeProposed(nextProposed);
        },
      )
      .addCase(resetIftaImport, () => initialIftaImportState);
  },
});

export const iftaImportReducer = iftaImportSlice.reducer;
export default iftaImportReducer;
